import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from '../translations';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

const { FiLock, FiCheckCircle, FiAlertCircle } = FiIcons;

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [validToken, setValidToken] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const checkResetToken = async () => {
      try {
        // HashRouter quirk: URL may be `/#/reset-password#access_token=...` (double hash)
        // window.location.hash returns the WHOLE thing starting at the first `#`
        // We must extract params from AFTER the LAST `#` (the Supabase-appended fragment).
        const fullHash = window.location.hash || '';
        const lastHashIdx = fullHash.lastIndexOf('#');
        const tokenFragment = lastHashIdx > 0
          ? fullHash.substring(lastHashIdx + 1)
          : fullHash.substring(1);
        const hashParams = new URLSearchParams(tokenFragment);
        const searchParams = new URLSearchParams(window.location.search);

        let accessToken = hashParams.get('access_token') || searchParams.get('access_token');
        let refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');
        let type = hashParams.get('type') || searchParams.get('type');

        console.log('Reset token check:', { accessToken: !!accessToken, type, url: window.location.href, fragment: tokenFragment.substring(0, 60) });

        if (accessToken && type === 'recovery') {
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || ''
            });

            if (error) {
              console.error('Session error:', error);
              throw error;
            }

            if (data?.user) {
              console.log('Valid recovery session established');
              setValidToken(true);
            } else {
              throw new Error('No user found in session');
            }
          } catch (sessionError) {
            console.error('Failed to establish session:', sessionError);
            setError('Invalid or expired reset link. Please request a new one.');
          }
        } else {
          console.log('No valid recovery token found');
          setError('Invalid reset link. Please request a new password reset from the login page.');
        }
      } catch (err) {
        console.error('Token check error:', err);
        setError('Unable to validate reset link. Please try again.');
      } finally {
        setCheckingToken(false);
      }
    };

    checkResetToken();
  }, [location]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!password || !confirmPassword) {
      setError(t('auth.error.emptyFields') || 'Please fill in all fields');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.error.passwordsDoNotMatch') || 'Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError(t('auth.error.passwordTooWeak') || 'Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      setSuccess(true);
      
      await supabase.auth.signOut();
      
      setTimeout(() => {
        navigate('/login?message=password-updated');
      }, 3000);
    } catch (err) {
      console.error('Password update error:', err);
      setError('Failed to reset password. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  const pageVariants = {
    initial: { opacity: 0, y: -20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: 20 },
  };

  if (checkingToken) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Validating reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4 overflow-hidden"
    >
      <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-600 rounded-full mix-blend-screen filter blur-xl opacity-30 animate-blob"></div>
      <div className="absolute top-0 -right-4 w-72 h-72 bg-blue-600 rounded-full mix-blend-screen filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 -left-20 w-72 h-72 bg-pink-600 rounded-full mix-blend-screen filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative z-10 w-full max-w-md p-8 space-y-6 bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20"
      >
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-100 tracking-wider">
            {success ? 'Password Reset Successful!' : validToken ? 'Create New Password' : 'Invalid Reset Link'}
          </h1>
          <p className="mt-2 text-gray-400">
            {success
              ? 'Your password has been updated successfully.'
              : validToken
              ? 'Enter your new password below.'
              : 'This reset link is invalid or has expired.'}
          </p>
        </div>

        {success ? (
          <div className="flex flex-col items-center space-y-4 py-8">
            <SafeIcon icon={FiCheckCircle} className="w-16 h-16 text-green-500" />
            <p className="text-center text-green-400 font-medium">
              Redirecting you to login...
            </p>
          </div>
        ) : !validToken ? (
          <div className="flex flex-col items-center space-y-4 py-8">
            <SafeIcon icon={FiAlertCircle} className="w-16 h-16 text-red-500" />
            <div className="text-center space-y-4">
              <p className="text-red-400">
                {error || 'This password reset link is invalid or has expired.'}
              </p>
              <button
                onClick={() => navigate('/forgot-password')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Request New Reset Link
              </button>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="flex items-center p-3 space-x-2 text-sm text-red-200 bg-red-500/20 rounded-lg">
                <SafeIcon icon={FiAlertCircle} className="w-5 h-5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute w-5 h-5 text-gray-400 top-3.5 left-4" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New Password"
                  className="w-full py-3 pl-12 pr-4 text-gray-200 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300"
                  required
                  minLength={6}
                />
              </div>

              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute w-5 h-5 text-gray-400 top-3.5 left-4" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm New Password"
                  className="w-full py-3 pl-12 pr-4 text-gray-200 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300"
                  required
                  minLength={6}
                />
              </div>

              {password && (
                <div className="text-xs text-gray-400">
                  Password strength:{' '}
                  <span
                    className={
                      password.length >= 12
                        ? 'text-green-400 font-semibold'
                        : password.length >= 8
                        ? 'text-yellow-400 font-semibold'
                        : 'text-red-400 font-semibold'
                    }
                  >
                    {password.length >= 12
                      ? 'Strong'
                      : password.length >= 8
                      ? 'Medium'
                      : 'Weak'}
                  </span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !validToken}
                className="w-full flex justify-center items-center py-3 px-4 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 transition-all duration-300 ease-in-out transform hover:scale-105"
              >
                {loading ? (
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : null}
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ResetPassword;